import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import {
  BULK_PAGE_SIZE,
  JOB_STATUS,
  buildDatePresets,
  buildRunPayload,
  canResume,
  canRetry,
  countRun,
  defaultBulkFilters,
  formatEtaCountdown,
  formatRangeLabel,
  itemToTableRow,
  newIdempotencyKey,
  pageInfo,
  percentOf,
  previewRowToTableRow,
  failedRetryCount,
  pausedCount,
  runDateLabel,
  stopReasonLabel,
} from "./model/bulkAnalyseModel";

/**
 * UI state for bulk analyse. Server state (the run, its rows, the preview)
 * lives in Redux and arrives through props from BulkAnalyse.container; this
 * hook only owns what is local to the modals (step, filters, page).
 */
export function useBulkAnalyseJob({
  enabled,
  pageRange,
  job,
  preview,
  previewLoading,
  previewError,
  createLoading,
  createError,
  stopLoading,
  stopError,
  retryLoading,
  dismissLoading,
  items,
  itemsPagination,
  itemsStatus,
  itemsLoading,
  jobOpen,
  loadActive,
  loadPreview,
  startRun,
  loadDetail,
  stopRun,
  retryRun,
  dismissBanner,
  setJobOpen,
  startPoll,
  stopPoll,
  clearCreateError,
}) {
  const presets = useMemo(
    () => buildDatePresets(moment(), pageRange),
    [pageRange?.from, pageRange?.to] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [step, setStepState] = useState(1);
  const [filters, setFilters] = useState(() => defaultBulkFilters(presets));
  const [page, setPageState] = useState(1);
  const [jobPage, setJobPageState] = useState(1);
  const [previewCause, setPreviewCause] = useState("filters");
  const idempotencyKeyRef = useRef(null);
  const wasCreatingRef = useRef(false);

  const running = job?.status === JOB_STATUS.RUNNING;
  const counts = countRun(job);
  const [etaWaited, setEtaWaited] = useState(0);

  // The estimate is rebuilt when a timesheet finishes. Until then, count
  // the seconds so "~51 sec left" moves every second.
  useEffect(() => {
    setEtaWaited(0);
    if (!running) return undefined;
    const timer = window.setInterval(() => setEtaWaited((seconds) => seconds + 1), 1000);
    return () => window.clearInterval(timer);
  }, [running, counts.remaining, job?.uuid]);

  // Restore the organisation's run after refresh, logout or a new tab.
  useEffect(() => {
    if (enabled) loadActive();
  }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fallback poll while a run is going, in case a Pusher event is missed.
  useEffect(() => {
    if (!enabled || !running) return undefined;
    startPoll();
    return () => stopPoll();
  }, [enabled, running, job?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Step 1 keeps the table in sync. Step 2 loads again on every visit so the
  // run totals and credit balance are current, not the last Step 1 page.
  useEffect(() => {
    if (!confirmOpen || (step !== 1 && step !== 2) || !filters.from || !filters.to) return;
    loadPreview(buildRunPayload(filters, { page, per_page: BULK_PAGE_SIZE }));
  }, [confirmOpen, step, filters, page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close the confirm modal once the run has started — or when another run
  // turned out to be active, which the saga opens instead.
  useEffect(() => {
    const finishedCreating = wasCreatingRef.current && !createLoading;
    if (finishedCreating && job?.uuid && (!createError || createError.code === "BULK_RUN_ACTIVE")) {
      setConfirmOpen(false);
    }
    wasCreatingRef.current = createLoading;
  }, [createLoading, createError, job?.uuid]);

  // The job modal can be opened from the saga (409); make sure it has rows.
  useEffect(() => {
    if (
      jobOpen &&
      job?.uuid &&
      jobPage === 1 &&
      !itemsLoading &&
      (items || []).length === 0 &&
      !itemsPagination?.total
    ) {
      loadDetail({ uuid: job.uuid, status: itemsStatus || "all", page: 1, perPage: BULK_PAGE_SIZE });
    }
  }, [jobOpen, job?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  const setStep = useCallback(
    (next) => {
      if (next === 2) {
        // One key per Summary visit: a double click or network retry of Run
        // all re-sends it, so the backend returns the same run.
        idempotencyKeyRef.current = newIdempotencyKey();
        setPreviewCause("filters");
      }
      clearCreateError();
      setStepState(next);
    },
    [clearCreateError]
  );

  const setFilter = useCallback(
    (patch) => {
      setFilters((current) => {
        const next = { ...current, ...patch };
        if (patch.presetId) {
          const preset = presets.find((item) => item.id === patch.presetId);
          if (preset) {
            next.from = preset.from;
            next.to = preset.to;
          }
        }
        return next;
      });
      setPreviewCause("filters");
      setPageState(1);
    },
    [presets]
  );

  const setPage = useCallback((nextPage) => {
    const pageNumber = Number(nextPage);
    if (!Number.isFinite(pageNumber) || pageNumber < 1) return;
    setPageState((current) => {
      if (current === pageNumber) return current;
      setPreviewCause("page");
      return pageNumber;
    });
  }, []);

  const openConfirm = useCallback(() => {
    setFilters(defaultBulkFilters(presets));
    setStepState(1);
    setPreviewCause("filters");
    setPageState(1);
    clearCreateError();
    setConfirmOpen(true);
  }, [clearCreateError, presets]);

  const closeConfirm = useCallback(() => setConfirmOpen(false), []);

  const previewCounts = preview?.counts || {};
  const maxTimesheets = Number(preview?.max_timesheets || 0);
  const scope = {
    matched: Number(previewCounts.matched || 0),
    pending: Number(previewCounts.pending || 0),
    alreadyAnalysed: Number(previewCounts.already_analysed || 0),
    willRun: Number(previewCounts.will_run || 0),
    tooMany: maxTimesheets > 0 && Number(previewCounts.will_run || 0) > maxTimesheets,
    maxTimesheets,
  };

  const previewRows = useMemo(
    () => (preview?.rows?.data || []).map(previewRowToTableRow),
    [preview]
  );
  const previewPage = pageInfo(preview?.rows?.pagination, previewRows.length);
  const confirmPage = { ...previewPage, rows: previewRows };

  const beginRun = useCallback(() => {
    if (
      !scope.willRun
      || scope.tooMany
      || createLoading
      || job?.status === JOB_STATUS.RUNNING
      || (job?.status === JOB_STATUS.STOPPED && !job?.banner_dismissed)
    ) return;
    startRun(
      buildRunPayload(filters, {
        idempotency_key: idempotencyKeyRef.current || newIdempotencyKey(),
      })
    );
  }, [createLoading, filters, job?.banner_dismissed, job?.status, scope.tooMany, scope.willRun, startRun]);

  const openJob = useCallback(() => {
    if (!job?.uuid) return;
    setJobPageState(1);
    loadDetail({ uuid: job.uuid, status: "all", page: 1, perPage: BULK_PAGE_SIZE });
    setJobOpen(true);
  }, [job?.uuid, loadDetail, setJobOpen]);

  const closeJob = useCallback(() => setJobOpen(false), [setJobOpen]);

  const changeJobFilter = useCallback(
    (status) => {
      if (!job?.uuid) return;
      setJobPageState(1);
      loadDetail({ uuid: job.uuid, status, page: 1, perPage: BULK_PAGE_SIZE });
    },
    [job?.uuid, loadDetail]
  );

  const setJobPage = useCallback(
    (nextPage) => {
      const pageNumber = Number(nextPage);
      if (!job?.uuid || !Number.isFinite(pageNumber) || pageNumber < 1 || pageNumber === jobPage) {
        return;
      }
      setJobPageState(pageNumber);
      loadDetail({
        uuid: job.uuid,
        status: itemsStatus,
        page: pageNumber,
        perPage: BULK_PAGE_SIZE,
      });
    },
    [itemsStatus, job?.uuid, jobPage, loadDetail]
  );

  const haltRun = useCallback(() => {
    if (job?.uuid && running && !stopLoading) stopRun(job.uuid);
  }, [job?.uuid, running, stopLoading, stopRun]);

  const resume = useCallback(() => {
    if (job?.uuid && canResume(job) && !retryLoading) retryRun(job.uuid, "paused");
  }, [job, retryLoading, retryRun]);

  const retry = useCallback(() => {
    if (job?.uuid && canRetry(job) && !retryLoading) retryRun(job.uuid, "failed");
  }, [job, retryLoading, retryRun]);

  const dismissFinished = useCallback(() => {
    if (job?.uuid && job.status !== JOB_STATUS.RUNNING && !dismissLoading) {
      dismissBanner(job.uuid);
    }
  }, [dismissBanner, dismissLoading, job?.status, job?.uuid]);

  const jobRows = useMemo(() => (items || []).map(itemToTableRow), [items]);
  const jobListPage = { ...pageInfo(itemsPagination, jobRows.length), rows: jobRows };

  const presetLabel =
    presets.find((item) => item.id === filters.presetId)?.label ||
    formatRangeLabel(filters.from, filters.to);

  return {
    // confirm modal
    confirmOpen,
    openConfirm,
    closeConfirm,
    step,
    setStep,
    filters,
    setFilter,
    presets,
    presetLabel,
    page,
    setPage,
    scope,
    confirmPage,
    options: preview?.options || { workers: [], participants: [] },
    balance: preview?.balance_aud ?? null,
    previewLoading,
    countsLoading: previewLoading && previewCause === "filters",
    previewError,
    createLoading,
    createError,
    beginRun,
    // run
    job,
    counts,
    percent: percentOf(counts),
    etaLabel: formatEtaCountdown(counts.remaining, running ? etaWaited : 0),
    resumeAllowed: canResume(job),
    resumeTotal: pausedCount(job),
    retryAllowed: canRetry(job),
    retryTotal: failedRetryCount(job),
    stopLabel: job?.status === JOB_STATUS.STOPPED ? stopReasonLabel(job.stop_reason) : "",
    dateLabel: runDateLabel(job),
    stopLoading,
    stopError: stopError || "",
    retryLoading,
    dismissLoading,
    haltRun,
    resume,
    retry,
    dismissFinished,
    // job modal
    jobOpen,
    openJob,
    closeJob,
    jobFilter: itemsStatus,
    changeJobFilter,
    jobPage,
    setJobPage,
    jobListPage,
    itemsLoading,
  };
}
